import { useState, useEffect, useMemo } from "react";
import BaseWrapper from "../../components/shared/BaseWrapper";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { HistoryEduOutlined } from "@mui/icons-material";
import { getActiveContracts } from "../API/billingAPI";

const CURRENCY = "PHP";
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const SORT_OPTIONS = {
  END_OLDEST: "end_oldest",
  END_NEWEST: "end_newest",
  CLIENT_ASC: "client_asc",
  CLIENT_DESC: "client_desc",
  PRICE_DESC: "price_desc",
  PRICE_ASC: "price_asc",
};

const parseDateValue = (value) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const compareText = (a = "", b = "") =>
  a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

const isExpiringSoon = (contract_end) => {
  if (!contract_end) return false;
  const today = new Date();
  const cutoff = new Date(today);
  cutoff.setMonth(cutoff.getMonth() + 2);

  const end = new Date(contract_end);
  return end >= today && end <= cutoff;
};

function ContractsView() {
  const [allContracts, setAllContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.END_OLDEST);
  const [showExpiringSoon, setShowExpiringSoon] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        let cursor = "";
        const contracts = [];
        do {
          const data = await getActiveContracts(200, cursor);
          if (!active) return;
          contracts.push(...(data?.items ?? []));
          cursor = data?.next_cursor || "";
        } while (cursor);

        const flat = contracts.map((contract) => ({
          contract,
          clientName: contract.client_name || contract.client?.name || "",
          clientId: contract.client_id || contract.client?.id || "",
        }));

        setAllContracts(flat);
      } catch {
        if (active) setAllContracts([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = allContracts;
    if (q) {
      result = result.filter(({ clientName }) =>
        (clientName || "").toLowerCase().includes(q)
      );
    }
    if (showExpiringSoon) {
      result = result.filter(({ contract }) =>
        isExpiringSoon(contract.contract_end)
      );
    }
    return result;
  }, [allContracts, searchQuery, showExpiringSoon]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (
        sortBy === SORT_OPTIONS.CLIENT_ASC ||
        sortBy === SORT_OPTIONS.CLIENT_DESC
      ) {
        const comparison = compareText(a.clientName, b.clientName);
        return sortBy === SORT_OPTIONS.CLIENT_ASC ? comparison : -comparison;
      }

      if (
        sortBy === SORT_OPTIONS.PRICE_DESC ||
        sortBy === SORT_OPTIONS.PRICE_ASC
      ) {
        const priceA = Number(a.contract.price_minor) || 0;
        const priceB = Number(b.contract.price_minor) || 0;
        return sortBy === SORT_OPTIONS.PRICE_DESC
          ? priceB - priceA
          : priceA - priceB;
      }

      const endA = parseDateValue(a.contract.contract_end);
      const endB = parseDateValue(b.contract.contract_end);
      if (endA === null && endB === null) {
        return compareText(a.clientName, b.clientName);
      }
      if (endA === null) return 1;
      if (endB === null) return -1;
      return sortBy === SORT_OPTIONS.END_NEWEST ? endB - endA : endA - endB;
    });
  }, [filtered, sortBy]);

  const totalPages = Math.ceil(sorted.length / rowsPerPage);
  const paged = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setPage(0);
  };

  const handleViewClick = ({ contract, clientName, clientId }) => {
    const contractId = contract.contract_id || contract.id;
    const storageKey = `contractsViewContract:${contractId}:${Date.now()}`;
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        contract,
        selectedClient: { id: clientId, name: clientName },
        source: "contractsView",
      })
    );
    window.open(
      `/view-contract/${contractId}?contractsViewContract=${encodeURIComponent(storageKey)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <BaseWrapper
      titleIcon={<HistoryEduOutlined />}
      title="Billing"
      subTitle="Contracts View"
      subTitleroute="/contracts-view"
    >
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search Client Name"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ minWidth: 310 }}
        />
        <Button
          size="small"
          variant={showExpiringSoon ? "contained" : "outlined"}
          color="warning"
          onClick={() => {
            setShowExpiringSoon((p) => !p);
            setPage(0);
          }}
        >
          Expiring
        </Button>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="contracts-sort-label">Sort by</InputLabel>
          <Select
            labelId="contracts-sort-label"
            label="Sort by"
            value={sortBy}
            onChange={handleSortChange}
          >
            <MenuItem value={SORT_OPTIONS.END_OLDEST}>
              End date: oldest first
            </MenuItem>
            <MenuItem value={SORT_OPTIONS.END_NEWEST}>
              End date: newest first
            </MenuItem>
            <MenuItem value={SORT_OPTIONS.CLIENT_ASC}>Client: A to Z</MenuItem>
            <MenuItem value={SORT_OPTIONS.CLIENT_DESC}>Client: Z to A</MenuItem>
            <MenuItem value={SORT_OPTIONS.PRICE_DESC}>
              Price: largest first
            </MenuItem>
            <MenuItem value={SORT_OPTIONS.PRICE_ASC}>
              Price: smallest first
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Contract ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length > 0 ? (
                  paged.map(({ contract, clientName, clientId }) => (
                    <TableRow key={contract.contract_id || contract.id} hover>
                      <TableCell>{clientName || "-"}</TableCell>
                      <TableCell>{contract.contract_id || contract.id}</TableCell>
                      <TableCell>{contract.contract_start || "-"}</TableCell>
                      <TableCell>{contract.contract_end || "-"}</TableCell>
                      <TableCell>
                        {CURRENCY}{" "}
                        {(contract.price_minor / 100).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() =>
                            handleViewClick({ contract, clientName, clientId })
                          }
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                      >
                        {searchQuery
                          ? "No active contracts match your search."
                          : "No active contracts found."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              py: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Results: {sorted.length} of {allContracts.length}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 135 }}>
              <InputLabel id="contracts-rows-label">Rows</InputLabel>
              <Select
                labelId="contracts-rows-label"
                label="Rows"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              >
                {ROWS_PER_PAGE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option} rows
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {totalPages > 1 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Back
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Page {page + 1} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </>
            )}
          </Box>
        </>
      )}
    </BaseWrapper>
  );
}

export default ContractsView;
